import { useEffect, useMemo, useRef, useState } from "react";

import styles from "./list.module.css";

import { MeetingOutputDTO, TimeEventTargetModels } from "@kalyzee/kast-web-sdk/http";
import { Button, useTriggerable } from "@kalyzee/kast-web-sdk/web-components";
import DatePicker from "react-datepicker";
import { PageMasterMode, usePageContextRef } from "../../../../components/navigation/PageContext";
import { useSettings } from "../../../../hooks/settings";

import { BoxOverlay, BoxOverlayProps, OverlayContainer, PrimaryButton, ReactIcons } from "@kalyzee/kalyzee-ui";
import { useTimeEventsOfTheDay } from "@kalyzee/kast-web-sdk";
import { MeetingState } from "@kalyzee/kast-web-sdk/websocket";
import { isToday } from "date-fns";
import { MasterUI } from "../../../../components/common/MasterUI";
import { MediaSelector } from "../../../../components/media/MediaSelector";
import { ErrorView } from "../../../../components/utils/ErrorView";
import { TimeEventsAgenda } from "../../../../components/utils/TimeEventsAgenda";
import { useAppStoreDispatch } from "../../../../contexts/appStore/context";
import { SessionMode } from "../../../../contexts/appStore/hooks";
import { useAppStoreSelector } from "../../../../contexts/appStore/selectors";
import { addPopup, removePopup } from "../../../../helpers/popup";
import { toastError } from "../../../../helpers/toast";
import { useSdkContext } from "../../../../contexts/sdk/context";

const AUTO_EVENT_SELECTION_FAVORED_AFTER_A_DELAY = 10 * 60 * 1000; // 10 minutes

export const MeetingListStudentPage = () => {
  const pageContextRef = usePageContextRef<SessionMode.MASTER>();
  const [date, setDate] = useState<Date>(new Date()); // new Date()

  const dispatch = useAppStoreDispatch();

  const user = useAppStoreSelector(state => state.user);
  const audioEnabled = useAppStoreSelector(state => state.audioStreamEnabled);
  const videoEnabled = useAppStoreSelector(state => state.videoStreamEnabled);
  const lastLeftMeetingId = useAppStoreSelector(state => state.lastLeftMeetingId);

  const inputAudioDeviceInfo = useAppStoreSelector(state => state.inputAudioDeviceInfo);
  const inputVideoDeviceInfo = useAppStoreSelector(state => state.inputVideoDeviceInfo);
  const outputAudioDeviceInfo = useAppStoreSelector(state => state.outputAudioDeviceInfo);

  const [selectedEventId, setSelectedEventId] = useState<string>();
  const selectedEventAtRef = useRef<Date>();
  const logoContainerRef = useRef<HTMLImageElement>(null);
  const [settings] = useSettings();
  const meetingId = pageContextRef.current?.searchParams?.get("meeting");

  const autojoin = pageContextRef.current?.searchParams?.get("autojoin") === "true";
  const canceledAutoJoinIds = useRef<Set<string>>(new Set());
  const mountTime = useRef<number>(Date.now());

  const sdkCtx = useSdkContext();

  const [mode, setMode] = useState<"meeting" | "list">(meetingId ? "meeting" : "list");
  const { timeEvents, currentTimeEvents, error, retry } = useTimeEventsOfTheDay(sdkCtx.client, date, user ? { type: "user", id: user._id }: undefined, false, {
    meeting: mode === "meeting" ? meetingId ?? undefined : undefined,
    targets: [TimeEventTargetModels.Meeting],
    populate: { room: true, actor: { identity: true } },
  });
  const currentTimeEvent = useMemo(() => currentTimeEvents.length ? currentTimeEvents[0] : undefined, [currentTimeEvents]);

  useTriggerable(logoContainerRef, { clickCount: 3 }, () => pageContextRef.current?.openSettingsPopup()); // press 'k' and click 3 times with mouse(left button) on logo

  // ---------------------------------------------------------------------- //
  // ---------------------------   UTILS ----------------------------- //
  // ---------------------------------------------------------------------- //

  const onJoinEvent = (eventId: string) => {
    const selectedEvent = timeEvents?.find((event) => event.id === eventId);
    const selectedMeeting = selectedEvent ? (selectedEvent.target as MeetingOutputDTO) : undefined;
    if (selectedMeeting) {
      pageContextRef.current?.goTo(
        PageMasterMode.MEETING_DASHBOARD, 
        { id: selectedMeeting._id },
        {
          queries: {
            id: undefined, // remove id from query params
          },
        }
      );
    }
  };

  // ---------------------------------------------------------------------- //
  // ---------------------------   EFFECT ----------------------------- //
  // ---------------------------------------------------------------------- //
  
  useEffect(() => {
    if (!autojoin) return;

    const allRunning = timeEvents?.filter(
      event => event.target.state === MeetingState.RUNNING
    );

    // If we just left a meeting, we cancel the events with auto-join to it
    // for 1 minute
    // When this time elapses, we'll accept new events
    // This is to avoid the case where [Meeting 1] was left manually,
    // but then we stay on the list page and [Meeting 1] comes back some time
    // later in an other event.
    if (lastLeftMeetingId && Date.now() - mountTime.current < 60 * 1000) {
      allRunning?.forEach(event => {
        if (event.target.id === lastLeftMeetingId) {
          canceledAutoJoinIds.current.add(event.id);
        }
      });
    }

    // Find first running
    const running = allRunning?.find(event => 
      !canceledAutoJoinIds.current.has(event.id)
      && event.target.id !== lastLeftMeetingId
    );

    if (!running) return;

    let currentPopup: React.ReactNode;
    let buttons: BoxOverlayProps["buttons"] = [
        {
          label: "Rejoindre",
          onClick: async () => {
            onJoinEvent(running.id);
          },
          validateAfter: 10,
        },
        {
          label: "Rester sur cette page",
          onClick: () => {
            // We had all currently running events to the list
            // so that user is not hit with many popups in a row,
            // his wish is to stay on the page and chose by himself
            allRunning?.forEach(event => {
              canceledAutoJoinIds.current.add(event.id);
            });
            removePopup(currentPopup);
          }
        }
    ];

    const meetingName = running.target.name;
    const title = meetingName 
      ? <>Le meeting <b>"{meetingName}"</b> est en cours</>
      : <>Un meeting est en cours</>;

    currentPopup = (
      <OverlayContainer
        style={{
          width: "100vw",
          height: "100vh",
        }}
        overlay={
          <BoxOverlay
            title={title}
            level={"info"}
            buttons={buttons}
          />
        }
      />
    );
    addPopup(currentPopup, "meeting_auto_join", "main");

    return () => {
      removePopup(currentPopup);
    };
  }, [timeEvents, lastLeftMeetingId]);

  useEffect(() => {
    if (!isToday(date)) return;
    // // check date every minute
    const interval = setInterval(() => {
      if (!isToday(date.getDate())) {
        setDate(new Date());
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [date]);

  useEffect(() => {
    const now = new Date();
    if (!selectedEventId && currentTimeEvent) setSelectedEventId(currentTimeEvent._id);
    else if (selectedEventAtRef.current) {
      const updateAfter = selectedEventAtRef.current.getTime() + AUTO_EVENT_SELECTION_FAVORED_AFTER_A_DELAY - now.getTime();
      if (updateAfter > 0) {
        const timeout = setTimeout(() => {
          if (currentTimeEvent) setSelectedEventId(currentTimeEvent._id);
        }, updateAfter);
        return () => {
          clearTimeout(timeout);
        };
      } else {
        if (currentTimeEvent) setSelectedEventId(currentTimeEvent._id);
      }
    }
  }, [currentTimeEvent, selectedEventId]);

  useEffect(() => {
    if (!selectedEventId) return;
    selectedEventAtRef.current = new Date();
  }, [selectedEventId]);


  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER ----------------------------- //
  // ---------------------------------------------------------------------- //

  if (error) return <ErrorView retry={() => { retry(); }} />;

  const renderEvents = () => {
    if (mode === "meeting") {
      // render only one meeting
      const timeEvent = timeEvents?.length ? timeEvents[0] : undefined;
      return (
        <div className={styles.eventsList}> 
          <TimeEventsAgenda
            timeEvents={timeEvent}
            placeholdersNb={1}
            selectedEventId={selectedEventId}
            onSelectEvent={setSelectedEventId}
            onJoinEvent={onJoinEvent}
            kind="student"
          />
        </div>
      );
    }
    return (
      <div className={styles.eventsList}>
        {settings.dev ? (
          <DatePicker
            showIcon
            selected={date}
            date={date ?? undefined}
            onChange={(d) => {
              if (!d) return;
              setDate(d);
            }}
          />
        ) : null}
        <TimeEventsAgenda
          timeEvents={timeEvents ? timeEvents.filter((event) => (event.target ? true : false)) : undefined}
          placeholdersNb={3}
          selectedEventId={selectedEventId}
          onSelectEvent={setSelectedEventId}
          onJoinEvent={onJoinEvent}
          kind="student"
        />
      </div>
    );
  };

  return (
    <MasterUI>
      <div className={styles.backgroundHome}>
        <div className={styles.mainHomeLogged}>
          <div className={styles.mainHomeLoggedContent}>
            <MediaSelector
              className={styles.mediaSelector}
              audioStreamEnabled={audioEnabled}
              videoStreamEnabled={videoEnabled}
              defaultInputAudioDeviceInfo={inputAudioDeviceInfo}
              defaultInputVideoDeviceInfo={inputVideoDeviceInfo}
              defaultOutputAudioDeviceInfo={outputAudioDeviceInfo}
              onInputAudioDeviceInfo={(device) => {
                if (device) dispatch("setInputAudioDeviceInfo", device);
              }}
              onInputVideoDeviceInfo={(device) => {
                if (device) dispatch("setInputVideoDeviceInfo", device);
              }}
              onOutputAudioDeviceInfo={(device) => {
                if (device) dispatch("setOutputAudioDeviceInfo", device);
              }}
              onAudioStreamEnabled={(enabled) => dispatch("setAudioStreamEnabled", enabled)}
              onVideoStreamEnabled={(enabled) => dispatch("setVideoStreamEnabled", enabled)}
              onMediaCaptureError={(type, error) => {
                if (type === "audio") {
                  toastError("Veuillez autoriser le navigateur à accéder au microphone");
                  dispatch("setAudioStreamEnabled", false);
                }
                if (type === "video") {
                  toastError("Veuillez autoriser le navigateur à accéder à la caméra");
                  dispatch("setVideoStreamEnabled", false);
                }
              }}
            />
            {renderEvents()}
            <PrimaryButton 
              nativeProps={{
                style: { position: "absolute", top: "1em", right: "1em" }
              }}
              onClick={() => pageContextRef.current?.goTo(PageMasterMode.REPLAY_LIST)} className={styles.replayBtn}>
              <ReactIcons.PlayScreens />
              Voir les replays
            </PrimaryButton>
          </div>
        </div>
      </div>
    </MasterUI>
  );
};
